"use client";

import { evaluate, toast } from "@kyan/ui";

import { getOptionsCreditAndDebit } from "#/app/(desktop)/combo/utils/getOptionsCreditAndDebit";
import { getPerpConstraints } from "#/config/orders";
import { useTradeButtonFeatureFlag } from "#/hooks/featureFlags/useTradeButtonFeatureFlag";
import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useDeltaSlippage } from "#/hooks/useDeltaSlippage";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { usePostComboOrder } from "#/services/kyan/hooks/usePostComboOrder";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import type { ComboInstrument } from "#/store/combo/comboInstruments";
import {
  defaultLimitNetPrices,
  useComboPrices,
} from "#/store/combo/comboPrices";
import { useComboState } from "#/store/combo/comboState";
import { useAccount } from "#/store/wallet/account/account";
import { ComboPricesRowType } from "#/types/combo";
import { type ComboTrade, MarketOrder } from "#/types/orders";
import { formatComboError } from "#/utils/formatComboError";
import {
  createOptionInstrumentName,
  createPerpInstrumentName,
  isCallOptionType,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { roundToNearest } from "#/utils/number.utils";

import { useCalculateUserRiskForComboOrderbook } from "../../PricesTable.hooks";

export const useComboTradeButtonController = (handleClose?: () => void) => {
  const { comboInstruments, handleClearSizes } =
    useComboInstrumentsController();
  const { comboPrices } = useCalculateUserRiskForComboOrderbook();
  const { refetch } = useRefetchAccountStateIfStale();
  const { isLiquidated } = usePortfolioValidations();
  const { getAction } = usePortfolioToastAction();
  const { isDevMode } = useIsDevMode();
  const { setComboState } = useComboState();
  const { setLimitNetPrices } = useComboPrices();
  const { isTradeButtonEnabled, tradeButtonTitle } =
    useTradeButtonFeatureFlag();

  const { mutate: postComboOrder, isPending } = usePostComboOrder({
    onSuccess: () => {
      toast.success({
        title: "Trade successful",
        action: getAction({ target: "index", label: "View in Portfolio" }),
      });

      setLimitNetPrices(defaultLimitNetPrices);
      setComboState("instruments");
      handleClearSizes();

      handleClose?.();

      refetch?.();
    },
    onError: (error: Error) => {
      toast.error({
        title: "Error with combo order",
        description: formatComboError(error),
      });
    },
  });
  const { account } = useAccount();
  const { limitNetPrices } = useComboPrices();
  const { hasEnoughLiquidity, hasSufficientMargin } =
    useCalculateUserRiskForComboOrderbook();
  const { areLimitNetPricesFilled } = useComboPrices();
  const deltaSlippageFactors = useDeltaSlippage();

  const shouldShowAlert = !hasEnoughLiquidity || !hasSufficientMargin;
  const hasValidationErrors = shouldShowAlert || !areLimitNetPricesFilled;
  const isDisabled =
    !isTradeButtonEnabled ||
    (!isDevMode && (hasValidationErrors || isLiquidated));

  const label = evaluate({
    [tradeButtonTitle]: !isTradeButtonEnabled,
    "Liquidation Active": isLiquidated,
    "Post FOK Combo Order": true,
  });
  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;

  const handleSubmit = () => {
    if (!account) return;

    const hasPerpsInstrument = comboInstruments.some(
      (instrument: ComboInstrument) => !isOptionInstrument(instrument.type),
    );

    const comboOptionPrices = comboPrices.filter(
      (instrument) => instrument.rowType === ComboPricesRowType.OptionsLeg,
    );

    const { isCredit } = getOptionsCreditAndDebit(
      comboOptionPrices,
      deltaSlippageFactors,
    );

    const createInstrumentName = (instrument: ComboInstrument) =>
      !isOptionInstrument(instrument.type)
        ? createPerpInstrumentName(instrument.underlying)
        : createOptionInstrumentName({
            ...instrument,
            isCall: isCallOptionType(instrument.optionType!),
          });

    const createMarketOrder = (instrument: ComboInstrument) => ({
      instrument_name: createInstrumentName(instrument),
      direction: instrument.side,
      contracts: Number(instrument.size),
    });

    const exponent = Math.pow(
      10,
      -getPerpConstraints(comboInstruments[0].underlying)
        .priceDisplayPrecisionNotional,
    );

    const payload: ComboTrade = {
      limit_total_net_premium: isCredit
        ? Number(limitNetPrices.summary)
        : -Number(limitNetPrices.summary),
      ...(hasPerpsInstrument && {
        limit_perp_price: roundToNearest(
          Number(limitNetPrices.perpsLeg),
          exponent,
        ),
      }),
      market_orders: comboInstruments.map(createMarketOrder),
      type: MarketOrder.FOK,
      taker: account,
    };

    postComboOrder(payload);
  };

  return {
    buttonLabel,
    isDisabled,
    isLoading: isPending,
    handleSubmit,
  };
};
